import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, Search, LayoutDashboard, User, LogIn, Bell } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
import { UserRole } from '../../types';

export const BottomNav: React.FC = () => {
  const { user } = useAuth();
  const { notifications } = useData();
  const location = useLocation();

  const unreadCount = notifications.filter(n => !n.read).length;

  const getDashboardLink = () => {
    if (!user) return '/login';
    switch (user.role) {
      case UserRole.STUDENT: return '/student/dashboard';
      case UserRole.AGENT: return '/agent/dashboard';
      case UserRole.LANDLORD: return '/landlord/dashboard';
      case UserRole.ADMIN: return '/admin/dashboard';
      default: return '/login';
    }
  };

  const isActive = (path: string) => {
    if (path === '/' && location.pathname === '/') return true;
    if (path !== '/' && location.pathname.startsWith(path)) return true;
    return false;
  };

  const baseClass = "flex flex-col items-center justify-center w-full h-full text-[10px] font-medium transition-colors gap-1";
  const activeClass = "text-[#2f5c96]";
  const inactiveClass = "text-gray-500 hover:text-gray-700";

  return (
    <div className="fixed bottom-0 left-0 z-50 w-full h-16 bg-white border-t border-gray-200 lg:hidden shadow-[0_-2px_10px_rgba(0,0,0,0.05)] pb-safe">
      <div className="grid grid-cols-5 h-full max-w-lg mx-auto font-sans">
        <Link to="/" className={`${baseClass} ${isActive('/') ? activeClass : inactiveClass}`}>
          <Home className={`w-6 h-6 ${isActive('/') ? 'fill-current' : ''}`} />
          <span>Home</span>
        </Link>
        <Link to="/browse" className={`${baseClass} ${isActive('/browse') ? activeClass : inactiveClass}`}>
          <Search className={`w-6 h-6 ${isActive('/browse') ? 'stroke-[2.5px]' : ''}`} />
          <span>Browse</span>
        </Link>
        <Link to="/notifications" className={`${baseClass} ${isActive('/notifications') ? activeClass : inactiveClass} relative`}>
          <Bell className={`w-6 h-6 ${isActive('/notifications') ? 'fill-current' : ''}`} />
          {unreadCount > 0 && (
            <span className="absolute top-2 right-4 flex h-4 w-4 items-center justify-center rounded-full bg-red-600 text-[10px] font-bold text-white border-2 border-white">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
          <span>Alerts</span>
        </Link>
        <Link
          to={getDashboardLink()}
          className={`${baseClass} ${location.pathname.includes('dashboard') || location.pathname === '/login' ? activeClass : inactiveClass}`}
        >
          {user ? (
            <LayoutDashboard className={`w-6 h-6 ${location.pathname.includes('dashboard') ? 'fill-current' : ''}`} />
          ) : (
            <LogIn className={`w-6 h-6 ${location.pathname === '/login' ? 'stroke-[2.5px]' : ''}`} />
          )}
          <span>{user ? 'Dashboard' : 'Login'}</span>
        </Link>
        <Link
          to={user ? "/profile" : "/register"}
          className={`${baseClass} ${isActive('/profile') || isActive('/register') ? activeClass : inactiveClass}`}
        >
          <User className={`w-6 h-6 ${isActive('/profile') ? 'fill-current' : ''}`} />
          <span>{user ? 'Profile' : 'Sign Up'}</span>
        </Link>
      </div>
    </div>
  );
};